<template>
    <div v-if="showWarning"
         class="bg-warning text-white py-3 px-6 lg:px-20 flex justify-between items-center">
        <div class="flex items-center gap-2 flex-1 justify-center">
            <span class="text-sm">System nie będzie dostępny w sobotę 23.01.2026 w godzinach od 01:00 do 06:00. Przepraszamy za utrudnienia.</span>
        </div>
        <button @click="showWarning = false"
                class="text-white hover:bg-gray-700 rounded-full p-2 transition-colors">
            <i class="pi pi-times text-sm"></i>
        </button>
    </div>

    <div class="bg-secondary-700 text-secondary-50 px-4 sm:px-6 lg:px-8 py-3 text-sm flex items-center gap-2 w-full shadow-inner">
        <i class="pi pi-home text-secondary-300"></i>
        <span class="text-secondary-400">/</span>
        <span>Pulpit</span>
    </div>

    <header class="relative bg-white shadow-sm">
        <div class="mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h1 class="text-3xl font-bold tracking-tight text-gray-900">Pulpit - moduł projektowy</h1>
        </div>
    </header>

    <div class="bg-gray-50 px-4 sm:px-6 py-8 md:px-12 lg:px-20">
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div class="bg-white shadow-sm p-6 rounded-lg border border-gray-100">
                <div class="flex justify-between items-start gap-4">
                    <div class="flex flex-col gap-2">
                        <span class="text-gray-600 font-medium text-sm">Messages</span>
                        <div class="text-gray-900 font-semibold text-3xl">152</div>
                    </div>
                    <div class="flex items-center justify-center bg-cyan-500 rounded-lg w-12 h-12 flex-shrink-0">
                        <i class="pi pi-envelope text-white text-xl" />
                    </div>
                </div>
                <div class="mt-4 text-sm">
                    <span class="text-gray-700 font-medium">24 new</span>
                    <span class="text-gray-500"> since last visit</span>
                </div>
            </div>

            <div class="bg-white shadow-sm p-6 rounded-lg border border-gray-100">
                <div class="flex justify-between items-start gap-4">
                    <div class="flex flex-col gap-2">
                        <span class="text-gray-600 font-medium text-sm">Check-ins</span>
                        <div class="text-gray-900 font-semibold text-3xl">532</div>
                    </div>
                    <div class="flex items-center justify-center bg-orange-500 rounded-lg w-12 h-12 flex-shrink-0">
                        <i class="pi pi-map-marker text-white text-xl" />
                    </div>
                </div>
                <div class="mt-4 text-sm">
                    <span class="text-gray-700 font-medium">48 new</span>
                    <span class="text-gray-500"> since last visit</span>
                </div>
            </div>

            <div class="bg-white shadow-sm p-6 rounded-lg border border-gray-100">
                <div class="flex justify-between items-start gap-4">
                    <div class="flex flex-col gap-2">
                        <span class="text-gray-600 font-medium text-sm">Files Synced</span>
                        <div class="text-gray-900 font-semibold text-3xl">28.441</div>
                    </div>
                    <div class="flex items-center justify-center bg-gray-500 rounded-lg w-12 h-12 flex-shrink-0">
                        <i class="pi pi-file text-white text-xl" />
                    </div>
                </div>
                <div class="mt-4 text-sm">
                    <span class="text-gray-500">32,56 / 250 GB</span>
                </div>
            </div>

            <div class="bg-white shadow-sm p-6 rounded-lg border border-gray-100">
                <div class="flex justify-between items-start gap-4">
                    <div class="flex flex-col gap-2">
                        <span class="text-gray-600 font-medium text-sm">Users Online</span>
                        <div class="text-gray-900 font-semibold text-3xl">25.660</div>
                    </div>
                    <div class="flex items-center justify-center bg-violet-500 rounded-lg w-12 h-12 flex-shrink-0">
                        <i class="pi pi-users text-white text-xl" />
                    </div>
                </div>
                <div class="mt-4 text-sm">
                    <span class="text-gray-700 font-medium">72 new</span>
                    <span class="text-gray-500"> user this week</span>
                </div>
            </div>
        </div>

        <!-- Recent Activity Table -->
        <div class="mt-8">
            <div class="bg-white shadow-sm rounded-lg border border-gray-100">
                <div class="px-6 py-4 border-b border-gray-100">
                    <h3 class="text-lg font-semibold text-gray-900">Recent Activity</h3>
                </div>
                <DataTable :value="recentActivities"
                           :paginator="true"
                           :rows="5"
                           :unstyled="false"
                           stripedRows>
                    <Column field="id"
                            header="ID"
                            sortable
                            style="width: 80px"></Column>
                    <Column field="user"
                            header="User"
                            sortable>
                        <template #body="slotProps">
                            <div class="flex items-center gap-3">
                                <div class="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center text-white text-xs font-medium">
                                    {{ slotProps.data.user.charAt(0) }}
                                </div>
                                <span>{{ slotProps.data.user }}</span>
                            </div>
                        </template>
                    </Column>
                    <Column field="action"
                            header="Action"
                            sortable></Column>
                    <Column field="date"
                            header="Date"
                            sortable>
                        <template #body="slotProps">
                            {{ formatDate(slotProps.data.date) }}
                        </template>
                    </Column>
                    <Column field="status"
                            header="Status"
                            sortable>
                        <template #body="slotProps">
                            <span :class="getStatusClass(slotProps.data.status)"
                                  class="px-3 py-1 rounded-full text-xs font-medium">
                                {{ slotProps.data.status }}
                            </span>
                        </template>
                    </Column>
                    <Column header="Actions"
                            style="width: 120px">
                        <template #body>
                            <div class="flex gap-2">
                                <Button icon="pi pi-eye"
                                        class="p-button-text p-button-sm"
                                        severity="info" />
                                <Button icon="pi pi-trash"
                                        class="p-button-text p-button-sm"
                                        severity="danger" />
                            </div>
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { setStoreValue } from '@pages/Helper/utils.js';
import Image from '@helper/Image.vue';
import { dashboardSplash } from '@helper/constants.js';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';

const props = defineProps({
    systemName: String,
    currentModule: String,
    systemData: Object,
});

const showWarning = ref(false);
const recentActivities = ref([
    {
        id: 1,
        user: 'John Doe',
        action: 'Created new project',
        date: '2025-12-19T10:30:00',
        status: 'Completed',
    },
    {
        id: 2,
        user: 'Jane Smith',
        action: 'Updated user profile',
        date: '2025-12-19T09:15:00',
        status: 'Completed',
    },
    {
        id: 3,
        user: 'Mike Johnson',
        action: 'Uploaded document',
        date: '2025-12-19T08:45:00',
        status: 'Pending',
    },
    {
        id: 4,
        user: 'Sarah Williams',
        action: 'Deleted old files',
        date: '2025-12-18T16:20:00',
        status: 'Completed',
    },
    {
        id: 5,
        user: 'Tom Brown',
        action: 'Added new team member',
        date: '2025-12-18T14:10:00',
        status: 'Completed',
    },
    {
        id: 6,
        user: 'Emily Davis',
        action: 'Generated report',
        date: '2025-12-18T11:30:00',
        status: 'Failed',
    },
    {
        id: 7,
        user: 'David Wilson',
        action: 'Modified settings',
        date: '2025-12-18T09:00:00',
        status: 'Completed',
    },
    {
        id: 8,
        user: 'Lisa Anderson',
        action: 'Sent notification',
        date: '2025-12-17T15:45:00',
        status: 'Pending',
    },
]);

const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getStatusClass = (status) => {
    const classes = {
        Completed: 'bg-green-100 text-green-800',
        Pending: 'bg-yellow-100 text-yellow-800',
        Failed: 'bg-red-100 text-red-800',
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
};

setStoreValue('currentModule', props.currentModule);
</script>

<style scoped></style>
